import { useCallback, useEffect, useMemo, useState } from 'react';
import { IFormProps } from '../../../../Components/CoreLib/library';
import { EventModel } from '@bryntum/scheduler';
import { usePatchOrderLineItemInlineUpdateByIdMutation, useUpdateDispatchAffectedByYardAndOnsiteTimeMutation } from '../../../../store/generated/generatedApi';
import { OrderLineItemDto, OrderLineItemInlineUpdateDto } from '../../../../dtos';
import { areDateTimesEqual } from '../../../../util';

export interface IOrderLineItemEditorFormValues extends IFormProps<EventModel> {
    driverMemo?: string;
    orderLineItemDto?: OrderLineItemDto;
}

export interface IOrderLineItemEditorFormValues {
    yardTime?: Date | null;
    onsiteTime?: Date | null;
    intervalTime?: number | null;
}

export function useOrderLineItemEditorForm(props: IOrderLineItemEditorFormValues) {
    const { save, cancel, initValues } = props;
    const [formDriverMemo, setFormDriverMemo] = useState(initValues && (initValues as any).data.driverMemo ? (initValues as any).data.driverMemo : '');
    const [formDispatchBillingMemo, setFormDispatchBillingMemo] = useState(
        initValues && (initValues as any).data.dispatchBillingMemo ? (initValues as any).data.dispatchBillingMemo : ''
    );
    const [formYardTime, setFormYardTime] = useState<Date | undefined | null>(
        initValues && (initValues as any).data.yardTime ? new Date((initValues as any).data.yardTime) : null
    );
    const [formOnsiteTime, setFormOnsiteTime] = useState<Date | undefined | null>(
        initValues && (initValues as any).data.onsiteTime ? new Date((initValues as any).data.onsiteTime) : null
    );
    const [formIntervalTime, setFormIntervalTime] = useState<number | undefined | null>(initValues ? (initValues as any).data.dispatchingIntervalTime : null);
    const [updateOrderLineItemInlineValues, { isLoading: isUpdating }] = usePatchOrderLineItemInlineUpdateByIdMutation();
    const [timesChanged, setTimesChanged] = useState(false);

    const [getAffectedDispatches, { data: affectedDispatches, isLoading: isGettingAffectedDispatches }] = useUpdateDispatchAffectedByYardAndOnsiteTimeMutation();

    useEffect(() => {
        if (initValues) {
            const event = (initValues as any).data;
            setFormDriverMemo(event?.driverMemo);
        }
    }, [initValues]);

    const isFormDirty = useMemo(() => {
        const data = (initValues as any).data;
        var isDirty = false;
        isDirty = isDirty || !areDateTimesEqual(formYardTime, data?.yardTime);
        isDirty = isDirty || !areDateTimesEqual(formOnsiteTime, data?.onsiteTime);
        isDirty = isDirty || formIntervalTime !== data?.dispatchingIntervalTime;
        isDirty = isDirty || formDriverMemo !== data?.driverMemo;
        isDirty = isDirty || formDispatchBillingMemo !== data?.dispatchBillingMemo;
        return isDirty;
    }, [initValues, formDriverMemo, formDispatchBillingMemo, formYardTime, formOnsiteTime, formIntervalTime]);

    const handleSave = useCallback(async () => {
        if (initValues) {
            save(initValues);
        }
    }, [initValues, save]);

    const updateOrderLineItemAndGetAffectedDispatches = useCallback(async () => {
        if (initValues) {
            let data = {
                resourceId: initValues.getData('resourceId'),
                dispatchBillingMemo: formDispatchBillingMemo,
                driverMemo: formDriverMemo,
                yardTime: formYardTime,
                onsiteTime: formOnsiteTime,
                dispatchingIntervalTime: formIntervalTime,
            };

            data.resourceId = initValues.getData('resourceId');
            initValues.set(data);

            const orderLineItem = initValues as unknown as OrderLineItemDto;

            const orderLineItemInlineUpdateDto: OrderLineItemInlineUpdateDto = {
                driverMemo: formDriverMemo,
                dispatchBillingMemo: formDispatchBillingMemo,
                yardTime: formYardTime ?? undefined,
                onsiteTime: formOnsiteTime ?? undefined,
                dispatchingIntervalTime: formIntervalTime ?? undefined
            };

            if (!isUpdating) {
                await updateOrderLineItemInlineValues({
                    params: {
                        id: orderLineItem.id
                    },
                    payload: orderLineItemInlineUpdateDto
                }).unwrap().then(async () => {
                    if (timesChanged) { 
                        await getAffectedDispatches([orderLineItem]).unwrap().then((data) => {
                            if (data && (data.length === 0)) {
                                handleSave();
                            }
                        });
                    } else {
                        handleSave();
                    }
                });
            }
        }
        
    }, [initValues, formDispatchBillingMemo, formDriverMemo, formYardTime, formOnsiteTime, formIntervalTime, isUpdating, updateOrderLineItemInlineValues, timesChanged, getAffectedDispatches, handleSave]);

    const handleCancel = useCallback(() => {
        cancel();
    }, [cancel]);

    return {
        isFormDirty,
        handleSave,
        handleCancel,
        setFormDispatchBillingMemo,
        setFormDriverMemo,
        setFormYardTime,
        setFormOnsiteTime,
        setFormIntervalTime,
        setTimesChanged,
        formDispatchBillingMemo,
        formDriverMemo,
        formYardTime,
        formOnsiteTime,
        formIntervalTime,
        timesChanged,
        affectedDispatches,
        isGettingAffectedDispatches,
        updateOrderLineItemAndGetAffectedDispatches
    };
}
